import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ReturnLabel } from "../../types";

type Props = {
  open: boolean;
  onClose: () => void;
  label: ReturnLabel | undefined;
};

export const ReturnLabelPreviewModal = ({ open, onClose, label }: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.returnLabels.previewModal",
  );
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const handleClose = () => {
    setImageLoading(true);
    setImageError(false);
    onClose();
  };

  const isImage = label?.contentType?.startsWith("image/");
  const isPdf = label?.contentType === "application/pdf";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" noWrap>
            {label?.fileName ?? translate("title")}
          </Typography>
          <Box flexGrow={1} />
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {!label ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : isPdf ? (
          <Box sx={{ height: "75vh" }}>
            <iframe
              src={label.url}
              title={label.fileName}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </Box>
        ) : isImage ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: 400,
              position: "relative",
            }}
          >
            {imageLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {imageError ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 400,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" color="error" gutterBottom>
                  {translate("loadError")}
                </Typography>
              </Box>
            ) : (
              <img
                src={label.url}
                alt={label.fileName}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: imageLoading ? "none" : "block",
                }}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageLoading(false);
                  setImageError(true);
                }}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {translate("previewUnavailable")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button onClick={handleClose} style={{ textTransform: "none" }}>
          {translateWithoutPrefix("buttons.close")}
        </Button>
        {label && (
          <Button
            variant="contained"
            href={label.url}
            target="_blank"
            rel="noopener noreferrer"
            download={label.fileName}
            startIcon={<DownloadIcon />}
            style={{ textTransform: "none" }}
          >
            {translate("download")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
